import React, { Suspense, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { CommentWithReplyField } from '@contently/toolkit';
import { useFetcher } from 'rest-hooks';
import useAvatarBadge from '../../hooks/useAvatarBadge';
import authorOwnerCheck from '../../utils/authorOwnerCheck';
import useUserContext from '../../hooks/useUserContext';
import CommentResource from '../../resources/CommentResource';
import ReplyChainContainer from './ReplyChainContainer';
import { getReplyCommentParams } from './utils';
import LoadingSpinner from '../../../utils/shared/LoadingSpinner';
import {
  updateResolvedAnnotation,
  updateAnnotationListCacheBust
} from '../../store/textEditorReducer';
import { ACTIVATE_ANNOTATION_EVENT } from '../../editor-ui';

export default function CommentWrapper({
  storyId,
  handleCancel,
  active,
  comment,
  users,
  filterState,
  onReplyMentionsFound
}) {
  const {
    id: commentId,
    authorable,
    authorIsEmployee,
    authorIsManagingEditor,
    resolved,
    highlightedText,
    body
  } = comment;
  const [updatedAt, setUpdatedAt] = useState(comment.updatedAt);
  const [resolveClicked, toggleResolveClicked] = useState(false);
  const { permissions } = useUserContext();
  const editComment = useFetcher(CommentResource.updateShape());
  const createComment = useFetcher(CommentResource.createShape());
  const dispatch = useDispatch();

  // State to track if any replies mention the current user
  const [hasReplyMentions, setHasReplyMentions] = useState(false);

  // Get current user for mention detection
  const currentUser = users && users.find(user => user.id === permissions.id);

  // Check if the comment body mentions the current user
  const commentMentionsUser =
    currentUser &&
    body &&
    (body.includes(`**${currentUser.display}**`) || body.includes(currentUser.display));

  // If we're in "Mentions Me" filter and neither the comment nor any replies mention the user,
  // don't render this component
  if (
    filterState === 'Mentions Me' &&
    ((!commentMentionsUser && !hasReplyMentions) || comment.resolved)
  ) {
    return null;
  }

  const handleEdit = (id, data) => {
    editComment({ id }, { body: data.edit }).then(({ updatedAt }) => {
      setUpdatedAt(updatedAt);
    });
  };

  const handleResolve = (e, id) => {
    if (e) e.preventDefault();
    if (comment.uuid) dispatch(updateResolvedAnnotation(comment.uuid));
    editComment({ id }, { resolved: 1 });
    dispatch(updateAnnotationListCacheBust(true));
    toggleResolveClicked(true);
  };

  const handleUnresolve = (e, id) => {
    if (e) e.preventDefault();
    if (comment.uuid) dispatch(updateResolvedAnnotation(comment.uuid));
    editComment({ id }, { resolved: 0 });
    dispatch(updateAnnotationListCacheBust(true));
    toggleResolveClicked(false);
  };

  const handleReply = (data, id) => {
    createComment({}, getReplyCommentParams(data, storyId, id), [
      [
        CommentResource.listShape(),
        { parent_id: id },
        (newCommentId, commentIds) => [...(commentIds || []), newCommentId]
      ]
    ]);
  };

  const [avatarTooltip, avatarBadge, avatarBadgeColor] = useAvatarBadge(
    authorable.fullName,
    authorIsEmployee,
    authorIsManagingEditor
  );

  const mark = document.querySelector(`#js-view-story-container [uuid="${comment.uuid}"]`);
  const annotationInStoryText = Boolean(mark);

  const handleClick = () => {
    if (annotationInStoryText) {
      const coordsRaw = mark.getBoundingClientRect();
      const coords = {
        top: coordsRaw.top,
        bottom: coordsRaw.bottom,
        left: coordsRaw.left,
        right: coordsRaw.right
      };
      const payload = { annotationUUID: comment.uuid, coords };
      const event = new CustomEvent(ACTIVATE_ANNOTATION_EVENT, { detail: payload });
      document.dispatchEvent(event);
    }
  };

  const showReplyForm = () => {
    if (active && !resolved && !resolveClicked && mark) {
      return false;
    } else if (active && !resolved && !resolveClicked) {
      return true;
    }
    return false;
  };

  const isAnnotationAndPresentInStory = () => {
    if (comment.uuid) return annotationInStoryText;

    return comment.isAnnotation;
  };

  // Convert comment.id to number if it's a string
  const commentIdNum = Number(commentId);

  // Update the parent when reply mentions change
  useEffect(() => {
    if (onReplyMentionsFound) {
      const commentIdNum = typeof commentId === 'string' ? parseInt(commentId, 10) : commentId;
      onReplyMentionsFound(commentIdNum, hasReplyMentions);
    }
  }, [hasReplyMentions, onReplyMentionsFound, commentId]);

  return (
    <div
      className={
        commentMentionsUser && filterState === 'Mentions Me' ? 'highlighted-mention-parent' : ''
      }
      data-comment-wrapper-id={commentIdNum}
    >
      <CommentWithReplyField
        {...comment}
        id={commentIdNum}
        updatedAt={updatedAt}
        fullName={authorable.fullName}
        avatar={authorable.avatar}
        authorIsOwner={authorOwnerCheck(authorable.id, permissions.id)}
        authorTooltip={avatarTooltip}
        authorBadge={avatarBadge}
        authorBadgeColor={avatarBadgeColor}
        resolved={resolved}
        highlightedText={highlightedText}
        onResolve={e => handleResolve(e, commentIdNum)}
        onUnresolve={e => handleUnresolve(e, commentIdNum)}
        allowUnresolve
        onReplyCancel={e => handleCancel(e)}
        onEditCancel={e => handleCancel(e)}
        onReplySubmit={e => handleReply(e, commentIdNum)}
        onEditSubmit={data => {
          handleEdit(commentIdNum, data);
        }}
        width="100%"
        showReplyForm={showReplyForm()}
        borderBottom={false}
        borderSides={false}
        users={users}
        borderLeft={isAnnotationAndPresentInStory()}
        inStoryText={annotationInStoryText}
        handleClick={handleClick}
      >
        <Suspense fallback={<LoadingSpinner />}>
          <ReplyChainContainer
            commentId={commentIdNum}
            showAllComments={active}
            enableEdit
            users={users}
            sidebar
            filterState={filterState}
            onMentionsFound={setHasReplyMentions}
          />
        </Suspense>
      </CommentWithReplyField>
    </div>
  );
}

CommentWrapper.defaultProps = {
  handleCancel: () => {},
  filterState: 'All comments'
};

CommentWrapper.propTypes = {
  storyId: PropTypes.number.isRequired,
  handleCancel: PropTypes.func,
  active: PropTypes.bool,
  comment: PropTypes.shape({
    id: PropTypes.number.isRequired,
    authorable: PropTypes.shape({
      id: PropTypes.number.isRequired,
      fullName: PropTypes.string.isRequired,
      avatar: PropTypes.string
    }).isRequired,
    body: PropTypes.string,
    highlightedText: PropTypes.string,
    resolved: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    authorIsEmployee: PropTypes.bool,
    authorIsManagingEditor: PropTypes.bool,
    uuid: PropTypes.string,
    isAnnotation: PropTypes.bool,
    updatedAt: PropTypes.string,
    superseded: PropTypes.bool
  }).isRequired,
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      display: PropTypes.string
    })
  ),
  filterState: PropTypes.string,
  onReplyMentionsFound: PropTypes.func
};



// WEBPACK FOOTER //
// ./app/javascript/client/components/textEditor/CommentWrapper.jsx